import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetBundle } from '~/hooks/apis/cruises/useGetBundle';
import useAppNavigation from '~/hooks/useAppNavigation';

import { BUNDLE_ELIGIBILITY_TYPES, BUNDLE_TYPES } from '../bundle-form-utils';
import BundleForm from '../components/BundleForm';

import BundlesFilterList from './BundlesFilter/BundlesFilterList';
import BundlesItemList from './BundlesItem/BundlesItemList';

function BundlesEdit() {
  const { bundleId } = useParams<{ bundleId: string }>();
  const { pathname } = useLocation();
  const { replace } = useAppNavigation();

  // Routes are declared with literal `/items` and `/filters` path segments
  // (see App.tsx), so derive the tab from the URL itself rather than useParams.
  const tab: 'items' | 'filters' | undefined = pathname.includes('/filters')
    ? 'filters'
    : pathname.includes('/items')
      ? 'items'
      : undefined;

  const { data: bundle, isLoading, error } = useGetBundle(bundleId);

  const showItems = bundle?.type === BUNDLE_TYPES.PRECONFIGURED;
  const showRates = bundle?.eligibilityType === BUNDLE_ELIGIBILITY_TYPES.EXPLICIT_RATES;
  const activeTab = showItems ? tab || 'items' : showRates ? 'filters' : undefined;

  useEffect(() => {
    if (bundleId && ((showItems && !tab) || (!showItems && showRates && tab !== 'filters'))) {
      replace(`/cruises/bundles/edit/${bundleId}/${showItems ? 'items' : 'filters'}`);
    }
  }, [tab, bundleId, showItems, showRates, replace]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Error loading bundle: {error.message}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Edit Bundle: {bundle?.name}</title>
      </Helmet>

      <PageHeader title={`Edit Bundle: ${bundle?.name || ''}`} backButton="/cruises/bundles" />

      {bundle && (
        <Box mt={1}>
          <Box>
            <BundleForm bundle={bundle} />
          </Box>
          {(showItems || showRates) && (
            <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
              <Tabs value={activeTab || false} variant="scrollable">
                {showItems && (
                  <Tab
                    value="items"
                    label="Bundle Items"
                    to={`/cruises/bundles/edit/${bundleId}/items`}
                    component={Link}
                  />
                )}
                <Tab
                  value="filters"
                  label={showItems ? 'Bundle Filters' : 'Select Rates'}
                  to={`/cruises/bundles/edit/${bundleId}/filters`}
                  component={Link}
                />
              </Tabs>

              <Box borderTop={1} borderColor="divider">
                {showItems && activeTab === 'items' && <BundlesItemList />}
                {showRates && activeTab === 'filters' && <BundlesFilterList vendorId={bundle.fkVendorId} />}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
}

export default BundlesEdit;
